import { StateParams, StateProvider, StateService } from "@uirouter/angularjs";
import {
  ReportingServer,
  vsName as ReportingServerName,
} from "../../reporting/server";

import { vsComponentName as SycuanMachinesComponentsViewsMainName } from "./components/views/main";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../records/views/recordGridView";
import { vsObjectName as machineName } from "./configs/machine";
import { vsComponentName as MachineConfigurationEditFormComponentName } from "./components/machineEditForm";
import { MachineAndConfigurationSycuan, MachineSycuan } from "./models/machine";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../records/forms/formView";
import { IServiceResponse, ServiceResponse } from "../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import { cloneDeep } from "lodash";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementService,
} from "../../ui/authorizedUiElement";
import { IQService, IScope } from "angular";
import { vsServiceName as MachineDetailTabsServiceName } from "../shared/components/views/machines/detail/tabs";
import { IReportGroup } from "../../reporting/interfaces/IReportGroup";
import {
  CloneAndConversionServiceSycuan,
  vsServiceName as CloneAndConversionServiceName,
} from "./services/cloneAndConversionService";
import { Machine } from "../shared/models/machine";
import { vsComponentName as softwareProgramGridComponentName } from "./components/softwareProgramGrid";

export function routeConfig($stateProvider: StateProvider) {
  $stateProvider
    .state("main.machines", {
      url: "machines",
      template: "<ui-view></ui-view>",
      abstract: true,
      resolve: {
        reportGroup: [
          ReportingServerName,
          function (reportingServer: ReportingServer) {
            return reportingServer.getReportGroup("machine").then((rg) => {
              return rg;
            });
          },
        ],
      },
    })
    .state("main.machines.softwareGrid", {
      url: "software",
      component: softwareProgramGridComponentName,
    })
    .state("main.machines.grid", {
      url: "",
      component: SycuanMachinesComponentsViewsMainName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          "$state",
          (
            recordGridViewService: RecordGridViewService,
            $state: StateService
          ) => {
            const config =
              recordGridViewService.getGridConfiguration(machineName);
            config!.onAddNew = () => {
              return $state.go("^.add").then(() => {});
            };

            return config;
          },
        ],
      },
    })
    .state("main.machines.add", {
      url: "/add",
      component: MachineConfigurationEditFormComponentName,
      params: {
        modelMachineId: undefined,
        modelMachineConfigurationId: undefined,
      },
      resolve: {
        model: [
          "$stateParams",
          CloneAndConversionServiceName,
          (
            $stateParams: StateParams,
            cloneAndConversionService: CloneAndConversionServiceSycuan
          ) => {
            const modelMachineId = $stateParams.modelMachineId;
            const modelMachineConfigurationId =
              $stateParams.modelMachineConfigurationId;

            if (!modelMachineId) {
              return {
                Configuration: { Status: "Floor" },
                Machine: {},
              };
            }

            const cloneConfiguration =
              cloneAndConversionService.getCloneConfiguration(
                modelMachineId,
                modelMachineConfigurationId
              );

            return cloneConfiguration;
          },
        ],
        machineFieldsEditable: [() => true],
        onSubmit: [
          FormViewServiceName,
          MachineDetailTabsServiceName,
          (
              formViewService: FormViewService,
              machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>
            ) =>
            (
              formViewController: FormViewController,
              model: MachineAndConfigurationSycuan
            ) =>
              formViewService
                .submitForm<ServiceResponse<MachineSycuan>>({
                  formObject: model,
                  formViewController,
                  method: "POST",
                  url: "/api/machines/Machine",
                })
                .then((result) =>
                  machineDetailTabsService.navigateToChildByIndex(0, {
                    machineId: result.Result.Id,
                  })
                ),
        ],
        onCancel: [
          "$state",
          ($state: StateService) => () => $state.go("^.grid"),
        ],
        formTitle: [() => "Add Machine"],
      },
    })

    .state("main.machines.detail", {
      url: "/{machineId:int}",
      template: require("!html-loader!./machineDetail.html").default,
      controller: [
        "$scope",
        "machine",
        "reportGroup",
        ($scope: IScope, machine: MachineSycuan, reportGroup: IReportGroup) => {
          const currentConfiguration = machine.Configs?.find(
            (c) => c.CurrentConfiguration
          );
          const s = <any>$scope;
          s.machine = machine;
          s.machineConfiguration = currentConfiguration;
          s.reportGroup = reportGroup;
          s.editSref = ".edit";
        },
      ],
      resolve: {
        machine: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const machineId = $stateParams["machineId"];
            return serverUtility
              .getById<IServiceResponse<MachineSycuan>>({
                url: "/api/machines/Machine",
                id: machineId,
              })
              .then((r) => r.Result);
          },
        ],
      },
    })
    .state("main.machines.detail.edit", {
      url: "/edit/{machineConfigurationId:int}",
      component: MachineConfigurationEditFormComponentName,
      resolve: {
        model: [
          "machine",
          "$stateParams",
          (machine: MachineSycuan, $stateParams: StateParams) => {
            const configurationId = $stateParams.machineConfigurationId;
            const configuration = machine.Configs?.find(
              (c) => c.Id === configurationId
            );

            const modelMachine = cloneDeep(machine);
            delete modelMachine.Configs;

            return {
              Machine: modelMachine,
              Configuration: cloneDeep(configuration),
            };
          },
        ],
        machineFieldsEditable: [() => true],
        onSubmit: [
          FormViewServiceName,
          MachineDetailTabsServiceName,
          "$q",
          (
            formViewService: FormViewService,
            machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
            $q: IQService
          ) => {
            return (formViewController: FormViewController, model: any) => {
              const { form } = formViewController;

              const machineId = model.Machine.Id;
              const configurationId = model.Configuration.Id;

              const updatePromises = [
                formViewService.submitForm({
                  formObject: model.Configuration,
                  formViewController: formViewController,
                  method: "PUT",
                  url: `/api/machines/MachineConfiguration/${configurationId}`,
                  validationPrefix: "Configuration",
                }),
              ];

              const anyDirtyMachineFields =
                form
                  .$getControls()
                  .find((f) => f.$name?.startsWith("Machine.") && f.$dirty) !==
                undefined;
              if (anyDirtyMachineFields) {
                updatePromises.push(
                  formViewService.submitForm({
                    formObject: model.Machine,
                    formViewController: formViewController,
                    method: "PUT",
                    url: `/api/machines/Machine/${machineId}`,
                    validationPrefix: "Machine",
                  })
                );
              }

              return $q
                .all(updatePromises)
                .then(() =>
                  machineDetailTabsService.navigateToChildByIndex(
                    0,
                    { machineId },
                    true
                  )
                );
            };
          },
        ],
        onCancel: [
          MachineDetailTabsServiceName,
          "$stateParams",
          (
              machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
              $stateParams: StateParams
            ) =>
            () => {
              return machineDetailTabsService.navigateToChildByIndex(0, {
                machineId: $stateParams.machineId,
              });
            },
        ],
        formTitle: [() => "Edit Machine"],
        isConversion: [
          "model",
          (model: MachineAndConfigurationSycuan) =>
            model.Configuration.ChangeType === "Conversion",
        ],
      },
    });
}
routeConfig.$inject = ["$stateProvider"];
